import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { Checkbox, DatePicker, Form, Input } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import SidePanel from 'common/side-panel/side-panel';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { Button, Div, Flex, FormField, Icon, Link } from 'ui';
import { connectionStates } from 'utils/app-configs';
import moment from 'utils/cops-moment';
import {
  calculateRouteOptimisation,
  fetchRecommendedEngineers,
} from '../../common/gantt/containers/gantt-actions';
import PlannerGroupToggle from '../../header/planner-group-toggle/components/planner-group-toggle';

const Warning = styled.div`
  color: ${p => p.theme.colors.alerts[2]} !important;
  font-weight: bold;
  margin: 20px 2px 10px 0;
  text-transform: uppercase;
  i {
    margin-right: 5px;
    color: ${p => p.theme.colors.alerts[2]} !important;
  }
`;

const StyledForm = styled(Form)`
  margin-top: 50px;
`;

const StyledFormField = styled(FormField)`
  width: 50%;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  margin-bottom: 15px;
`;

const RecommendedEngineersList = styled(Div)`
  margin-top: 20px;
`;

const SearchInput = styled(Input)`
  width: 150px;
  border-radius: 5px !important;
  margin: 3px 0;
  height: 18px !important;
  font-size: 14px !important;
`;

const ShowButton = styled(Link)`
  margin-top: 15px;
`;

const OpenIcon = styled.span`
  cursor: pointer;
  margin-right: 5px;
  > span
    {
      width: 12px;
    }
`;

const SmallSpinner = styled(Icon)`
  margin-left: 5px;
`;

const BigSpinner = styled(Icon)`
  font-size: 50px;
`;

const SpinnerWrapper = styled(Div)`
  width: 50px;
`;

const StyledButton = styled(Button)`
  text-transform: uppercase;
`;

const ScrollZone = styled(Div)`
  height: 300px;
  overflow: auto;
`;

const ButtonWrapper = styled(Flex)`
  gap: 10px;
`;

const StyledIcon = styled(Icon)`
  margin-right: 8px;
  color: white;
`;

const startPaging = 10;

const RouteOptimisationSidePanel = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const intl = useIntl();

  const fromValue = useWatch('from', form);
  const toValue = useWatch('to', form);
  const serviceCenterId = JSON.parse(localStorage.getItem('serviceCenter'))?.value || '';
  const preferredPlannerGroupByServiceCenterId = JSON.parse(
    localStorage.getItem('preferredPlannerGroupByServiceCenterId') || {},
  );
  const [plannerGroup, setPlannerGroup] = useState(
    preferredPlannerGroupByServiceCenterId[serviceCenterId],
  );
  const [recommendedEngineersList, setRecommendedEngineersList] = useState([]);
  const [numberOfItemsToShow, setNumberOfItemsToShow] = useState(startPaging);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState();
  const connection = useSelector(state => state.sockets.connection);
  const recommendedEngineersListIsLoading =
    useSelector(state => state.gantt.recommendedEngineersListIsLoading);
  const enabledRecommendedEngineersList = recommendedEngineersList?.filter(t => t.isRecommended);

  const selectedRecommendedEngineersList =
    recommendedEngineersList?.filter(i =>
      // eslint-disable-next-line no-undefined
      i.checked || (i.checked === undefined && i.isRecommended));

  useEffect(() => {
    if (plannerGroup) {
      dispatch(fetchRecommendedEngineers({
        callback: (data) => {
          if (!data?.error) {
            setNumberOfItemsToShow(startPaging);
            setRecommendedEngineersList(data);
          }
        },
        plannerGroup,
      }));
    }
  }, [plannerGroup]);

  const goBack = () => {
    const url = window.location.pathname;
    const planningBoardUrl = url.replace('/route-optimisation', '');
    if (planningBoardUrl) {
      navigate(planningBoardUrl);
    }
  };

  const showMore = () => {
    setNumberOfItemsToShow(s => s + 10);
  };

  const showLess = () => {
    setNumberOfItemsToShow(startPaging);
  };

  const calculate = () => {
    dispatch(calculateRouteOptimisation({
      callback: (data) => {
        if (!data?.error) {
          goBack();
        }
      },
      from: fromValue,
      to: toValue,
      technicianIds: selectedRecommendedEngineersList?.map(i => i.technicianId),
    }));
  };

  const searchRecommendedEngineer = (p) => {
    setRecommendedEngineersList(recommendedEngineersList?.map(engineer => ({
      ...engineer,
      hidden: p === '' ? false : !engineer.technicianName?.toLowerCase()?.includes(p),
    }),
    ));
  };

  const selectRecommendedEngineer = (p) => {
    setRecommendedEngineersList(recommendedEngineersList?.map((engineer) => {
      if (engineer.technicianName === p.technicianName && engineer.isRecommended) {
        return {
          ...engineer,
          checked: engineer.checked !== undefined ? !engineer.checked : false,
        };
      }
      return engineer;
    }));
  };

  const selectAllEngineers = (p) => {
    setRecommendedEngineersList(recommendedEngineersList?.map(engineer =>
      ({ ...engineer, checked: engineer.isRecommended ? p : false })));
  };

  const renderSpinners = () => recommendedEngineersListIsLoading && <>
    <SmallSpinner className='fa-solid fa-spinner fa-spin' size='xs' />
    <SpinnerWrapper><BigSpinner /></SpinnerWrapper>
  </>;

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    searchRecommendedEngineer(value?.toLowerCase());
  };

  const renderToolbar = () => {
    const recommendedEngineersListIsEmpty = recommendedEngineersList?.length === 0;
    const checkedList =
      recommendedEngineersList.filter(engineer => engineer.checked === true ||
        (engineer.checked === undefined && engineer.isRecommended));

    return (
      <>
        <Div onClick={() => !recommendedEngineersListIsEmpty && setOpen(s => !s)}>
          {recommendedEngineersListIsEmpty ?
            null :
            <OpenIcon>{open ? <DownOutlined /> : <RightOutlined />}</OpenIcon>
          }
          {intl.formatMessage(
            { id: 'planningBoard.routeOptimisation.AVAILABLE_RECOMMENDED_ENGINEERS' },
          )}
          {!recommendedEngineersListIsEmpty &&
            <>
              {' '}({selectedRecommendedEngineersList?.length}{' '}
              {intl.formatMessage({ id: 'planningBoard.routeOptimisation.SELECTED' })})
            </>
          }
          {renderSpinners()}
        </Div>
        {!recommendedEngineersListIsEmpty && open &&
          <Div>
            <Checkbox
              checked={checkedList.length &&
                checkedList.length === enabledRecommendedEngineersList?.length}
              indeterminate={checkedList.length < enabledRecommendedEngineersList?.length &&
                checkedList.length}
              onChange={() => selectAllEngineers(
                checkedList.length !== enabledRecommendedEngineersList?.length ||
                !checkedList.length,
              )}
            >
              {intl.formatMessage({ id: 'planningBoard.routeOptimisation.SELECT_ALL' })}
            </Checkbox>
            <SearchInput
              value={searchValue}
              onChange={handleSearch}
              placeholder={intl.formatMessage({
                id: 'planningBoard.routeOptimisation.SEARCH_NAME',
              })}
            />
          </Div>}
      </>
    );
  };

  return (
    <SidePanel
      width={'40%'}
      title={intl.formatMessage({ id: 'planningBoard.routeOptimisation.TITLE' })}
      onPanelClose={goBack}
    >
      <div>
        {intl.formatMessage({ id: 'planningBoard.routeOptimisation.EXPLANATION' })}
      </div>
      <StyledForm
        layout='vertical'
        form={form}
        requiredMark={true}
      >
        <StyledFormField
          name='from'
          label={intl.formatMessage({ id: 'planningBoard.routeOptimisation.FROM' })}
          rules={[{
            required: true,
          }]}
        >
          <StyledDatePicker
            suffixIcon={<DownOutlined />}
            format={'DD/MM/YYYY'}
            defaultPickerValue={moment().add(3, 'week')}
            disabledDate={current =>
              current.isBefore(moment().add(3, 'week'), 'day') ||
              current.isAfter(moment().add(6, 'week'), 'day')
            }
            allowClear={false}
            disabled={recommendedEngineersListIsLoading}
            getPopupContainer={p => document.getElementsByClassName('fullscreen')[0] ?? p}
          />
        </StyledFormField>
        <StyledFormField
          name='to'
          label={intl.formatMessage({ id: 'planningBoard.routeOptimisation.TO' })}
          rules={[{
            required: true,
          }]}
        >
          <StyledDatePicker
            suffixIcon={<DownOutlined />}
            format={'DD/MM/YYYY'}
            defaultPickerValue={fromValue?.add(1, 'day')}
            disabledDate={current =>
              current.isBefore(moment().add(1, 'day'), 'day') || // disable today
              // current falls before one day after fromValue or after 3 weeks from fromValue
              current.isBefore(fromValue?.add(1, 'day'), 'day') ||
              current.isAfter(fromValue?.add(3, 'week'), 'day')
            }
            allowClear={false}
            disabled={recommendedEngineersListIsLoading || !fromValue}
            getPopupContainer={p => document.getElementsByClassName('fullscreen')[0] ?? p}
          />
        </StyledFormField>
        <StyledFormField
          name='plannerGroup'
          label={intl.formatMessage({ id: 'planningBoard.routeOptimisation.PLANNERGROUP' })}
        >
          <PlannerGroupToggle
            onChange={(value) => {
              setPlannerGroup(value);
              setSearchValue();
            }}
            extraStyles={{ width: '50%' }}
            disabled={recommendedEngineersListIsLoading}
          />
        </StyledFormField>
      </StyledForm>
      <RecommendedEngineersList>
        {renderToolbar()}
        <ScrollZone>
          {open && recommendedEngineersList
            ?.filter(engineer => !engineer.hidden)
            ?.slice(0, numberOfItemsToShow).map(
              engineer => <Div key={engineer.technicianName}>
                <Checkbox
                  key={engineer.technicianName}
                  checked={engineer.checked ?? engineer.isRecommended}
                  disabled={!engineer.isRecommended}
                  onChange={() => selectRecommendedEngineer(
                    engineer,
                  )}
                >
                  {engineer.technicianName}
                </Checkbox>
              </Div>,
            )}
          {open && numberOfItemsToShow < recommendedEngineersList?.filter(e => !e.hidden).length &&
            <ShowButton onClick={showMore}>
              {intl.formatMessage({ id: 'planningBoard.routeOptimisation.SHOW_MORE' })}
            </ShowButton>
          }
          {open && startPaging !== numberOfItemsToShow &&
            <ShowButton onClick={showLess}>
              {intl.formatMessage({ id: 'planningBoard.routeOptimisation.SHOW_LESS' })}
            </ShowButton>
          }
        </ScrollZone>
      </RecommendedEngineersList>
      {connection?.state !== connectionStates.Connected &&
        <Warning>
          <Icon type='triangle-exclamation' size='lg' />
          {intl.formatMessage({ id: 'planningBoard.routeOptimisation.SOCKET_CONNECTION_LOST' })}
        </Warning>
      }
      <ButtonWrapper>
        <StyledButton
          light={true}
          type='secondary'
          onClick={goBack}
        >
          {intl.formatMessage({ id: 'common.CANCEL' })}
        </StyledButton>
        <StyledButton
          type='secondary'
          onClick={calculate}
          disabled={
            recommendedEngineersListIsLoading ||
            !fromValue ||
            !toValue ||
            !selectedRecommendedEngineersList?.length ||
            connection?.state !== connectionStates.Connected
          }
        >
          {intl.formatMessage({ id: 'planningBoard.routeOptimisation.CALCULATE' })}
        </StyledButton>
      </ButtonWrapper>
    </SidePanel >
  );
};

const RouteOptimisationButton = () => {
  const navigate = useNavigate();
  const intl = useIntl();

  return <StyledButton
    type='secondary'
    onClick={() => navigate('route-optimisation')}
  >
    <StyledIcon small={true} type='wand-magic-sparkles' />
    {intl.formatMessage({ id: 'planningBoard.routeOptimisation.TITLE' })} ...
  </StyledButton>;
};

export { RouteOptimisationButton, RouteOptimisationSidePanel };

